/**
 * Maps i18n language code to a BCP 47 locale for date formatting.
 * Use with toLocaleDateString/toLocaleString/Intl.DateTimeFormat.
 */
const LOCALE_MAP = {
  es: 'es-AR',
  en: 'en-US',
  pt: 'pt-BR',
};

export function getDateLocale(language) {
  return LOCALE_MAP[language] || language || 'es-AR';
}

/**
 * Parses a timestamp tolerantly. Handles real ISO strings, epoch numbers, and
 * Date instances — plus the malformed `...HH:mm:ss<seq>Z` form that the Citame
 * demo seed emits as a WhatsApp message sort key (e.g. "2026-06-10T09:50:00000Z"
 * = seconds "00" + 3-digit sequence "000" + "Z"). `new Date()` rejects that
 * shape, which is why the Conversations view used to render the raw ISO string.
 * Returns a valid Date, or null when unparseable.
 */
export function parseTimestamp(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const str = String(value);
  let d = new Date(str);
  if (!Number.isNaN(d.getTime())) return d;
  // Normalize: date T HH:mm:ss <extra digits> [tz]. The extra digits are treated
  // as milliseconds, which also preserves the seed's per-message ordering.
  const m = str.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2}):(\d{2})(\d*)(Z|[+-]\d{2}:?\d{2})?$/);
  if (m) {
    const [, date, hh, mm, ss, frac, tz] = m;
    const ms = (frac || '').slice(0, 3).padEnd(3, '0');
    d = new Date(`${date}T${hh}:${mm}:${ss}.${ms}${tz || 'Z'}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Locale-aware relative timestamp for chat / message UIs (es-AR by default):
 *   hoy        -> "14:35"
 *   ayer       -> "Ayer 14:35"
 *   esta semana-> "Lun 14:35"
 *   más viejo  -> "10/06/2026 14:35"
 * `t` is the i18n translator (for the "Ayer" label); falls back to "Ayer".
 */
export function formatMessageTimestamp(value, language, t, now = new Date()) {
  const d = parseTimestamp(value);
  if (!d) return typeof value === 'string' ? value : '';
  const locale = getDateLocale(language);
  // hour12:false fuerza 24h (es-AR canónico) de forma determinística — algunos
  // builds de ICU rinden es-AR en 12h con sufijo "a. m.".
  const time = d.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  if (isSameDay(d, now)) return time;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, yesterday)) {
    const label = (t && t('common.yesterday')) || 'Ayer';
    return `${label} ${time}`;
  }

  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays >= 0 && diffDays < 7) {
    let weekday = d.toLocaleDateString(locale, { weekday: 'short' }).replace(/\.$/, '');
    weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
    return `${weekday} ${time}`;
  }

  const date = d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${date} ${time}`;
}
